import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import clsx from 'clsx'
import { CaretDown } from 'phosphor-react'
import { ReactComponent as Add } from '../../../../../assets/svgs/add.svg'
import { Button } from '../../../../../components/Button'
import { FormGroup } from '../../../../../components/FormElements/FormGroup'
import { Input } from '../../../../../components/FormElements/Input'
import { InfiniteSelect } from '../../../../../components/InfiniteSelect'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../constants/toast'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { api } from '../../../../../services/api'
import { useTaskCreateMutation } from '../../../../../services/hooks/tasks/mutations/useTaskCreateMutation'
import { useTaskDeleteMutation } from '../../../../../services/hooks/tasks/mutations/useTaskDeleteMutation'
import {
  useTaskDoneMutation,
  useTaskUndoneMutation,
} from '../../../../../services/hooks/tasks/mutations/useTaskDoneMutation'
import { useTaskLinkCollaboratorMutation } from '../../../../../services/hooks/tasks/mutations/useTaskLinkCollaboratorMutation'
import { useTaskUnlinkCollaboratorMutation } from '../../../../../services/hooks/tasks/mutations/useTaskUnlinkCollaboratorMutation'
import { useTaskUpdateMutation } from '../../../../../services/hooks/tasks/mutations/useTaskUpdateMutation'
import { type AdministrativeUserDomain } from '../../../../../types/domain/administrative-users-domain'
import { type ExpertDomain } from '../../../../../types/domain/expert-domain'
import { type ProjectDomain } from '../../../../../types/domain/project-domain'
import { type TaskCollaboratorDomain } from '../../../../../types/domain/task-collaborator-domain'
import { type TaskDomain } from '../../../../../types/domain/task-domain'
import { type TaskPersistence } from '../../../../../types/persistence/task-persistence'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'
import { invalidateQueriesList } from '../../../../../utils/invalidateQueriesList'
import taskSchema from '../../../../../validators/taskSchema'
import { CollaboratorButton } from '../CollaboratorButton'
import { CommentsContainer } from './Comments/CommentsContainer'
import { DeleteTaskModal } from './DeleteTaskModal'

type TaskFormProps = {
  selectedTask?: TaskDomain
  project: ProjectDomain
  onClose?: () => void
}

type FormValues = {
  name: string
  expertId: number
  administrativeUserEmail: string
  projectId: number
}

export const TaskForm = ({ project, selectedTask, onClose }: TaskFormProps) => {
  const queryClient = useQueryClient()
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)

  const [searchExpert, setSearchExpert] = useState('')
  const [searchCollaborator, setSearchCollaborator] = useState('')
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<TaskCollaboratorDomain>()
  const [collaboratorBox, setCollaboratorBox] = useState<boolean>(true)
  const [searchAdmininistrativeUser, setSearchAdministrativeUser] = useState('')

  const navigate = useNavigate()
  const [isCommentsVisible, setIsCommentsVisible] = useState(true)
  const [isBeingDeletedTask, setIsBeingDeletedTask] = useState(false)
  const [displayedCollaborators, setDisplayedCollaborators] = useState<
    Array<{
      name: string
      email: string
    }>
  >([])
  const [temporaryCollaborators, setTemporaryCollaborators] = useState<
    Array<{
      name: string
      email: string
    }>
  >([])

  const { mutate: doneTaskMutation } = useTaskDoneMutation()
  const { mutate: undoneTaskMutation } = useTaskUndoneMutation()
  const { mutate: createTaskMutation, isLoading: isCreatingTask } =
    useTaskCreateMutation()
  const { mutate: deleteTaskMutation, isLoading: isDeletingTask } =
    useTaskDeleteMutation()
  const { mutate: updateTaskMutation, isLoading: isUpdatingTask } =
    useTaskUpdateMutation()
  const {
    mutate: linkCollaboratorToTaskMutation,
    isLoading: isLinkingCollaborator,
    mutateAsync: linkCollaboratorToTaskMutationAsync,
  } = useTaskLinkCollaboratorMutation()
  const { mutate: unlinkCollaboratorFromTaskMutation } =
    useTaskUnlinkCollaboratorMutation()
  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(taskSchema),
    defaultValues: {
      name: selectedTask?.name,
      expertId: selectedTask?.expert?.id,
      projectId: project.id,
      administrativeUserEmail:
        selectedTask?.admin !== null
          ? selectedTask?.admin?.email
          : selectedTask?.researcher?.email,
    },
  })

  const {
    data: administrativeUsers,
    hasNextPage: administrativeUsersHasNextPage,
    fetchNextPage: fetchNextAdministrativeUsersPage,
  } = useInfiniteQuery(
    ['administrative-users', searchAdmininistrativeUser],
    async ({ pageParam = 1 }) => {
      const url = `/users/administratives?page=${pageParam}&search=${searchAdmininistrativeUser}&sort=name&order=ASC`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const allAdministrativeUsers: AdministrativeUserDomain[] = useMemo(() => {
    return (
      administrativeUsers?.pages?.reduce(
        (acc: AdministrativeUserDomain[], { data }) => [...acc, ...data],
        []
      ) || []
    )
  }, [administrativeUsers])

  const administrativeUsersOptions = useMemo(() => {
    const options = allAdministrativeUsers.map(({ name, email }) => ({
      label: name,
      value: email,
    }))

    const administrativeUser = selectedTask?.admin || selectedTask?.researcher
    const administrativeUserAlreadySelected = options.some(
      (option) => option.value === administrativeUser?.email
    )

    if (administrativeUser && !administrativeUserAlreadySelected) {
      options.push({
        label: administrativeUser.name,
        value: administrativeUser.email,
      })
    }

    return [{ label: 'Sem researcher', value: '' }, ...options]
  }, [allAdministrativeUsers, selectedTask])

  const {
    data: experts,
    hasNextPage: hasNextExpertsPage,
    fetchNextPage: fetchNextExpertsPage,
  } = useInfiniteQuery(
    ['experts-select', searchExpert],
    async ({ pageParam = 1 }) => {
      const url = `/experts?page=${pageParam}&search=${searchExpert}&sort=name&order=ASC`
      const response = await api.get(url)
      return response.data
    },
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const expertsOptions = useMemo(() => {
    const allExperts: ExpertDomain[] =
      experts?.pages?.reduce((acc, { data }) => [...acc, ...data], []) || []

    const options = allExperts.map(({ name, id }) => ({
      label: name,
      value: String(id),
    }))

    const expertAlreadySelected = options.some(
      (option) => option.value === String(selectedTask?.expert?.id)
    )

    if (selectedTask?.expert && !expertAlreadySelected) {
      options.push({
        label: selectedTask.expert.name,
        value: String(selectedTask.expert.id),
      })
    }

    return [{ label: 'Sem expert', value: '' }, ...options]
  }, [experts, selectedTask])

  const {
    data: collaboratorData,
    hasNextPage: collaboratorHasNextPage,
    fetchNextPage: collaboratorFetchNextPage,
  } = useInfiniteQuery(
    ['collaborator-select', searchCollaborator],
    ({ pageParam = 1 }) =>
      api
        .get(
          `users/collaborators-suggestions?page=${pageParam}&search=${searchCollaborator}&sort=name&order=ASC`
        )
        .then((response: any) => response.data),
    {
      getNextPageParam({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const collaboratorOptions = useMemo(() => {
    const allCollaborators =
      collaboratorData?.pages.reduce(
        (acc, { data }) => [...acc, ...data],
        []
      ) || []

    const options = allCollaborators.map(
      ({ name, email }: TaskCollaboratorDomain) => ({
        label: name,
        value: String(email),
        name,
        email,
      })
    )

    const collaboratorsAlreadySelected = options.filter((option: any) =>
      displayedCollaborators.some(
        (collaborator) => collaborator.email === option.email
      )
    )

    collaboratorsAlreadySelected.forEach((collaborator: any) => {
      const index = options.findIndex(
        (option: any) => option.email === collaborator.email
      )
      options.splice(index, 1)
    })

    return options
  }, [collaboratorData, displayedCollaborators])

  const handleSetCollaborator = (value: string) => {
    setSelectedCollaborator(
      collaboratorOptions?.find(
        (collaborator: any) => String(collaborator.value) === value
      )
    )
  }

  useEffect(() => {
    if (selectedTask?.collaborators) {
      setDisplayedCollaborators(
        selectedTask.collaborators.map((collaborator) => {
          return { name: collaborator.name, email: collaborator.email }
        })
      )
    }
  }, [selectedTask])

  const handleAddCollaborator = () => {
    if (selectedCollaborator && !selectedTask?.id) {
      if (
        temporaryCollaborators.find(
          (item) => item.email === selectedCollaborator.email
        )
      ) {
        toast.error('Colaborador já adicionado', TOAST_ERROR_DEFAULT_OPTIONS)
        return
      }
      setDisplayedCollaborators((prevState) => [
        ...prevState,
        {
          name: selectedCollaborator.name,
          email: selectedCollaborator.email,
        },
      ])
      setTemporaryCollaborators((prevState) => [
        ...prevState,
        {
          name: selectedCollaborator.name,
          email: selectedCollaborator.email,
        },
      ])
      handleSetCollaborator('')
      return
    }
    if (selectedCollaborator && selectedTask?.id) {
      linkCollaboratorToTaskMutation(
        {
          id: selectedTask?.id,
          data: { collaboratorEmail: selectedCollaborator.email },
        },
        {
          onSuccess() {
            invalidateQueriesList(queryClient, ['tasks', 'my-tasks'])

            toast.success(
              'Colaborador adicionado com sucesso',
              TOAST_SUCCESS_DEFAULT_OPTIONS
            )

            setDisplayedCollaborators((prevState) => [
              ...prevState,
              {
                name: selectedCollaborator.name,
                email: selectedCollaborator.email,
              },
            ])

            handleSetCollaborator('')
          },
          onError(error: any) {
            if (error?.response?.status === 409) {
              toast.error(
                'Colaborador já adicionado',
                TOAST_ERROR_DEFAULT_OPTIONS
              )
            } else {
              toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
            }
          },
        }
      )
    }
  }

  const handleRemoveCollaborator = (email: string) => {
    if (!selectedTask?.id) {
      setDisplayedCollaborators((prevState) =>
        prevState.filter((item) => item.email !== email)
      )
      setTemporaryCollaborators((prevState) =>
        prevState.filter((item) => item.email !== email)
      )
      return
    }

    unlinkCollaboratorFromTaskMutation(
      {
        id: selectedTask?.id,
        data: { collaboratorEmail: email },
      },
      {
        onSuccess() {
          invalidateQueriesList(queryClient, ['tasks', 'my-tasks'])

          toast.success(
            'Colaborador removido com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setDisplayedCollaborators((prevState) =>
            prevState.filter((item) => item.email !== email)
          )
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }
  const handleAddCollaborators = async (taskId: number) => {
    if (temporaryCollaborators.length > 0) {
      await Promise.all(
        temporaryCollaborators.map(async (collaborator) => {
          await linkCollaboratorToTaskMutationAsync({
            id: taskId,
            data: { collaboratorEmail: collaborator.email },
          })
        })
      )
    }
  }

  const handleCreateTask = async (data: TaskPersistence) => {
    if (selectedTask?.id) {
      updateTaskMutation(
        { taskId: selectedTask?.id, data },
        {
          onSuccess() {
            invalidateQueriesList(queryClient, ['tasks', 'my-tasks'])
            toast.success(
              'Task atualizada com sucesso',
              TOAST_SUCCESS_DEFAULT_OPTIONS
            )

            navigate(`/painel/projects/${project?.id}`)
            onClose?.()
          },
          onError(error) {
            toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
          },
        }
      )
      return
    }

    createTaskMutation(
      { ...data },
      {
        async onSuccess(data: TaskDomain) {
          await handleAddCollaborators(data.id)
          toast.success(
            'Task criada com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
          invalidateQueriesList(queryClient, ['tasks', 'my-tasks'])

          navigate(`/painel/projects/${project?.id}`)
          onClose?.()
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  const handleDeleteTask = (id: number) => {
    deleteTaskMutation(id, {
      onSuccess() {
        onClose?.()
        invalidateQueriesList(queryClient, ['tasks', 'my-tasks'])
        toast.success(
          'Task excluída com sucesso!',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )
      },
      onError(error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleCompleteTask = () => {
    doneTaskMutation(selectedTask!.id, {
      async onSuccess() {
        invalidateQueriesList(queryClient, [
          ['project', project.id],
          'tasks',
          'my-tasks',
        ])

        toast.success(
          'Task concluída com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        onClose?.()
      },
      onError(error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const handleUndoneTask = () => {
    undoneTaskMutation(selectedTask!.id, {
      async onSuccess() {
        invalidateQueriesList(queryClient, [
          ['project', project.id],
          'tasks',
          'my-tasks',
        ])

        toast.success(
          'Task reaberta com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        onClose?.()
      },
      onError(error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  return (
    <div className="flex flex-col gap-4 px-3 lg:px-6 pt-2 pb-8 lg:py-8 lg:max-h-[calc(100vh_-_100px)] lg:overflow-y-auto lg:scrollbar">
      {!isMinScreenWidthLG && (
        <p className="mb-4 text-base font-semibold break-words  text-grey-900">
          {selectedTask?.name}
        </p>
      )}

      <form onSubmit={handleSubmit(handleCreateTask)}>
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex-1">
            <Controller
              name="name"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <Input
                  label="Task"
                  errorMessage={error?.message}
                  autoFocus
                  placeholder="Task"
                  isValid={!invalid && !!error}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <div className="flex-1">
            <Controller
              name="administrativeUserEmail"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <FormGroup label="Researcher" error={error?.message}>
                  <InfiniteSelect
                    placeholder="Selecione o researcher"
                    searchInputPlaceholder="Buscar pelo researcher"
                    defaultValue={String(value)}
                    hasNextPage={administrativeUsersHasNextPage}
                    options={administrativeUsersOptions}
                    onLoadMoreOptions={fetchNextAdministrativeUsersPage}
                    onValueChange={onChange}
                    onSearch={setSearchAdministrativeUser}
                  />
                </FormGroup>
              )}
            />
          </div>

          <div className="flex-1">
            <Controller
              name="expertId"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <FormGroup label="Expert" error={error?.message}>
                  <InfiniteSelect
                    placeholder="Selecione o expert"
                    searchInputPlaceholder="Buscar pelo expert"
                    defaultValue={String(value)}
                    hasNextPage={hasNextExpertsPage}
                    options={expertsOptions}
                    onLoadMoreOptions={fetchNextExpertsPage}
                    onValueChange={onChange}
                    onSearch={setSearchExpert}
                  />
                </FormGroup>
              )}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 mt-2 py-5 px-4 border-[1px] border-solid border-grey-200 rounded-lg">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setCollaboratorBox(!collaboratorBox)}
          >
            <p className="text-sm font-medium ">Colaboradores</p>
            <CaretDown
              className={clsx('transition transform ease-in-out', {
                'rotate-180': collaboratorBox,
              })}
            />
          </div>
          {collaboratorBox && (
            <div className="mt-4">
              <div className="flex items-end gap-3">
                <div className="w-[415px]">
                  <div className="flex-1">
                    <InfiniteSelect
                      key={displayedCollaborators.length}
                      placeholder="Selecione o colaborador"
                      searchInputPlaceholder="Buscar pelo colaborador"
                      hasNextPage={collaboratorHasNextPage}
                      options={collaboratorOptions}
                      onLoadMoreOptions={collaboratorFetchNextPage}
                      onValueChange={(value) => {
                        handleSetCollaborator(value)
                      }}
                      onSearch={setSearchCollaborator}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  size="large"
                  variant="outline"
                  onClick={handleAddCollaborator}
                  className="whitespace-nowrap h-12 py-[25px]"
                  isLoading={isLinkingCollaborator}
                >
                  <Add />
                  Adicionar colaborador
                </Button>
              </div>
              <div className="flex gap-2 mt-10 mb-5">
                {displayedCollaborators?.map((collaborator) => (
                  <CollaboratorButton
                    key={collaborator.email}
                    collaborator={collaborator}
                    onRemove={() =>
                      handleRemoveCollaborator(collaborator.email)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            disabled={!formState.isValid || !formState.isDirty}
            isLoading={isCreatingTask || isUpdatingTask}
          >
            Salvar
          </Button>
        </div>
      </form>

      {selectedTask && (
        <>
          <CommentsContainer
            taskId={selectedTask.id}
            isOpen={isCommentsVisible}
            onToggleOpen={() => setIsCommentsVisible((prevState) => !prevState)}
          />
          <div className="flex gap-2 mx-auto mt-10 lg:mx-0 w-fit">
            {selectedTask.done ? (
              <Button type="button" onClick={handleUndoneTask}>
                Reabrir Task
              </Button>
            ) : (
              <Button type="button" onClick={handleCompleteTask}>
                Concluir Task
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsBeingDeletedTask(true)}
            >
              Excluir task
            </Button>
          </div>

          <DeleteTaskModal
            isOpen={isBeingDeletedTask}
            onClose={() => setIsBeingDeletedTask(false)}
            onDelete={() => handleDeleteTask(selectedTask.id)}
            isRemoving={isDeletingTask}
          />
        </>
      )}
    </div>
  )
}
